import React, { useEffect, useRef, useState, useMemo } from "react";

import styles from "./styles.module.css";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  getAllStatusOptions,
  getPatientStatuses,
  createPatientStatus,
  updatePatientStatus,
} from "../../../services/patient/settings";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "../../ui/badge";
import { FaLock } from "react-icons/fa";
import toast from "react-hot-toast";
import Button from "../../button-v1";
import Spinner from "../../spinner";
// Import the admin AddStatusModal component
import AddStatusModal from "../../admin/statuses/addStatusModal";
import { FaPlus } from "react-icons/fa6";

const schemaValidation = z.object({
  status: z.number({
    invalid_type_error: "Status is required",
  }),
  description: z.string().min(1, "Description is required"),
});

const defaultSchema = {
  status: "",
  description: "",
};

const AddStatus = ({
  isOpen,
  onClose,
  statusId,
  setStatusId,
  onReopenModal,
}) => {
  const queryClient = useQueryClient();

  // Add state for admin status modal
  const [isAdminStatusModalOpen, setIsAdminStatusModalOpen] = useState(false);

  const patientId = sessionStorage.getItem("_pt_id");
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: defaultSchema,
    resolver: zodResolver(schemaValidation),
  });

  const status = watch("status");

  const {
    data: availableStatuses,
    isLoading: isLoadingGlobalStatuses,
    error: optionsError,
    refetch: refetchGlobalStatuses,
  } = useQuery(["global-statuses"], () => getAllStatusOptions(), {
    onError: (err) => {
      console.error("Could not load statuses:", err);
      toast.error("Could not load status. Please try again.");
    },
  });

  const { data: patientStatuses, isLoading: isPatientStatusesLoading } =
    useQuery(["patient-statuses"], () => getPatientStatuses(), {
      onError: (err) => {
        console.error("Error fetching status options:", err);
        toast.error("Could not load status. Please try again.");
      },
    });

  const createStatusMutation = useMutation(
    (data) => createPatientStatus(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["patient-statuses"]);
        onClose();
        toast.success("Status assigned to patient successfully");
        setStatusId(null);
        reset(defaultSchema);
      },
      onError: (error) => {
        console.error("Could not create status:", error);
        toast.error("Failed to assign status. Please try again.");
      },
    }
  );

  const updateStatusMutation = useMutation(
    (data) => updatePatientStatus(statusId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["patient-statuses"]);
        onClose();
        setStatusId(null);
        toast.success("Patient status updated successfully");
      },
      onError: (error) => {
        console.error("Could not update status:", error);
        toast.error("Could not update status. Please try again.");
      },
    }
  );

  const submitHandler = (values) => {
    if (statusId) {
      updateStatusMutation.mutate(values);
    } else {
      createStatusMutation.mutate(values);
    }
  };

  // Compute filtered statuses: exclude those already assigned to the patient, memoized for efficiency
  const assignedStatusIds = useMemo(
    () => patientStatuses?.map((s) => s.status) || [],
    [patientStatuses]
  );
  const filteredStatuses = useMemo(
    () => availableStatuses?.filter((s) => !assignedStatusIds.includes(s.id)),
    [availableStatuses, assignedStatusIds]
  );

  useEffect(() => {
    if (!isPatientStatusesLoading && patientStatuses?.length > 0) {
      const patientStatus = patientStatuses.find(
        (_status) => _status.id == statusId
      );

      reset(patientStatus);
    }
  }, [statusId]);

  useEffect(() => {
    if (!status) return;

    const description = filteredStatuses.find(
      (_status) => _status.id === status
    )?.description;

    if (!description) return;

    setValue("description", description);
  }, [status, filteredStatuses, setValue]);

  // Handle opening admin status modal
  const handleOpenAdminStatusModal = () => {
    onClose(); // Close current modal
    setIsAdminStatusModalOpen(true); // Open admin status modal
  };

  // Handle closing admin status modal
  const handleCloseAdminStatusModal = () => {
    setIsAdminStatusModalOpen(false);
    // Refetch global statuses to get the newly created status
    refetchGlobalStatuses();
    // Reopen the current modal after a short delay
    if (onReopenModal) {
      setTimeout(() => {
        onReopenModal();
      }, 100);
    }
  };

  return (
    <form onSubmit={handleSubmit(submitHandler)} id="addStatusForm">
      <Dialog open={isOpen} setOpen={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {statusId ? "Update Status" : "Assign Status"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            {isLoadingGlobalStatuses ? (
              <p className="content-center">
                <Spinner />
              </p>
            ) : (
              <div className={styles.input_wrapper}>
                <div className={styles.input_group}>
                  <div className={styles.input_field}>
                    <label>Status</label>
                    <Select
                      onChange={(value) => setValue("status", value)}
                      value={status}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a status">
                          <p>
                            {filteredStatuses?.find(
                              (_status) => _status.id === status
                            )?.name || "Select status"}
                          </p>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {filteredStatuses
                          ?.filter((_status) => _status.is_default)
                          .map((status) => (
                            <SelectItem value={status.id} key={status.id}>
                              <section className={styles.default_status}>
                                <Badge title="Default/System generated status">
                                  <FaLock size={10} />
                                </Badge>
                                {status.name}
                              </section>
                            </SelectItem>
                          ))}
                        {filteredStatuses
                          ?.filter((_status) => !_status.is_default)
                          .map((status) => (
                            <SelectItem key={status.id} value={status.id}>
                              {status.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {errors?.status && (
                      <p className="error-message">{errors?.status?.message}</p>
                    )}
                  </div>
                  <section className={styles.action}>
                    <Button onClick={handleOpenAdminStatusModal} type="button">
                      <FaPlus /> Add
                    </Button>
                  </section>
                </div>
                <div className={styles.input_group}>
                  <div className={styles.input_wrapper}>
                    <label>Description</label>
                    <textarea
                      {...register("description")}
                      rows={4}
                      placeholder="Enter a description for this status"
                    />
                    {errors?.description && (
                      <p className="error-message">
                        {errors?.description?.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onClose()}>
              Cancel
            </Button>
            <Button
              // onClick={handleSubmit(submitHandler)}
              type="submit"
              form="addStatusForm"
              loading={
                createStatusMutation?.isLoading ||
                updateStatusMutation?.isLoading // disabled={
                //   createStatusMutation.isLoading ||
                //   updateStatusMutation.isLoading ||
                //   (!isEditing && isStatusAlreadyAssigned(selectedStatus))
                // }
              }
              // disabled={
              //   createStatusMutation.isLoading ||
              //   updateStatusMutation.isLoading ||
              //   (!isEditing && isStatusAlreadyAssigned(selectedStatus))
              // }
            >
              {statusId ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Status Modal */}
      <AddStatusModal
        isOpen={isAdminStatusModalOpen}
        onClose={handleCloseAdminStatusModal}
        comp_renderer={() => {}} // Empty function since we handle refetch manually
        current_render={false}
      />
    </form>
  );
};

export default AddStatus;
